<!--
  FusionMain.vue
  ==============
  Sole Vue entry point for all routes. Wraps FusionToolbar or FusionController
  and handles all authentication client-side via AJAX.

  Auth can come from two sources:
  1. URL parameters (?jwt=XXX&cif=YYY) - checked first in created() hook
  2. postMessage from parent (UPDATE-CONTEXT with JWT) - fallback if no URL params

  Both paths use the same authenticateViaAjax flow, which validates JWT,
  performs trusted login, and fetches UserAccessInfo.

  Children (FusionToolbar, FusionController) remain completely unchanged.
-->
<template>
  <div id="fusion_main">
    <!-- Toolbar mode: always render so the right column icons are always visible -->
    <fusion-toolbar v-if="is_toolbar" key="fusion_main_toolbar" :set-configuration="tool_configuration"
                    :set-token="auth_token" :auth-state="toolbar_auth_state" />

    <!-- Controller mode: only render once auth is resolved -->
    <template v-else-if="!is_trusted_login">
      <fusion-controller v-if="auth_ready" key="fusion_main_controller" :set-configuration="tool_configuration"
                         :set-token="auth_token" />
      <!-- Auth in progress. [NPM-72] auth_failed is PROVISIONAL while retry polling runs (nine of
           the ten sites that set it immediately start polling), so the refusal is keyed on
           auth_rejected, not on the raw state. After ~5s a soft hint explains the wait. -->
      <div v-else-if="!auth_rejected" class="flex flex-col items-center justify-center w-full py-10 gap-3" style="min-height: 300px;">
        <div class="fusion-loader" />
        <span v-if="auth_hint_visible" class="text-sm text-text-subtle select-none">{{ $t('common.connecting') }}</span>
      </div>
      <!-- Auth failed -->
      <p v-else class="w-full text-center text-text-default py-5 select-none" style="min-height: 300px;" @click="handleUnauthorizedClick">{{ $FUSunauthorizedMessage }}</p>
    </template>

    <!-- TRUSTED_LOGIN: show status messages matching server-side responses -->
    <template v-else>
      <p v-if="auth_state === 'client_ready'">OK</p>
      <p v-else-if="auth_state === 'auth_failed'">JWT token is invalid</p>
      <p v-else>JWT token not processed</p>
    </template>

    <!-- Debug log modal: easter egg — click unauthorized message 10 times (every multiple of 10) -->
    <dialog-modal :isOpen="show_debug_modal" :title="$t('debug.authLogsTitle')" variant="1" @close="show_debug_modal = false">
      <div>
        <p v-if="debug_logs.length === 0">{{ $t('debug.noLogs') }}</p>
        <template v-else>
          <button class="text-xs mb-2 cursor-pointer underline text-text-subtle" @click="copyDebugLogs">{{ debug_log_copy_label }}</button>
          <pre class="overflow-auto text-xs">{{ debug_logs_text }}</pre>
        </template>
      </div>
    </dialog-modal>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import FusionToolbar from '@components/Core/FusionToolbar.vue';
import FusionController from '@components/Core/FusionController.vue';
import DialogModal from '@components/UI/DialogModal.vue';
import { isTrustedHostMessage } from '../../utils/embedOrigins';

// [NPM-72] How long retry polling runs before the loader gains a "Connecting…" hint.
const AUTH_HINT_DELAY_MS = 5000;
// [NPM-110] A published token this close to `exp` is treated as expired rather than posted:
// covers clock drift between host and Fusion and the round-trip to Authorize itself.

export default {
  props: {
    /** Base64-encoded tools configuration from server */
    setConfiguration: {
      type: String,
      required: false,
      default: null
    },
    entryType: {
      type: String,
      required: true,
      validator: (value) => ['TOOLBAR', 'CONTROLLER', 'TRUSTED_LOGIN'].includes(value)
    },
    /** Fusion UI release version injected server-side from RuntimeSystemConfig.ReleaseVersion */
    fusionReleaseVersion: {
      type: String,
      required: false,
      default: null
    },
    statusMessages: {
      type: String,
      required: false,
      default: null
    },
    // Base64-encoded Fusion JWT from the OAuth door's session. When present, FusionMain
    // authenticates with it directly instead of waiting for a host-app postMessage. Empty string
    // when the toolbar is loaded by a host app (which delivers JWT via postMessage or URL hash).
    sessionJwt: {
      type: String,
      required: false,
      default: ''
    },
  },
  components: {
    'fusion-toolbar': FusionToolbar,
    'fusion-controller': FusionController,
    'dialog-modal': DialogModal,
  },
  created() {
    // Non-reactive getUserAccessInfo generation counter. Each getUserAccessInfo call stamps a
    // generation; a response that lands after a newer call or a user switch (which bumps the
    // generation) is stale and must neither overwrite user_access_info nor post MCP-ACCESS. The
    // caller detects the supersession and bails. Kept off the reactive tree on purpose (a Promise
    // here would be proxied by data() and throw when awaited); the `|| 0` fallbacks below mean an
    // undefined counter can never become NaN.
    this._uaiGeneration = 0;

    // Store the server-injected UI release version so MessengerWindow can include it in messages
    if (this.fusionReleaseVersion)
      this.mainStore.fusion_release_version = this.fusionReleaseVersion;

    if (this.statusMessages && this.statusMessages !== 'null')
      this.mainStore.status_messages = JSON.parse(this.$FUSdecodeIfBase64(this.statusMessages));

    // Initialize theme (centralized here so both children share the same init)
    this.mainStore.initTheme();

    // Same for language — restores the sessionStorage locale (and starts listening for sibling-frame
    // switches) so the first paint is in the user's language. applyProfileLanguage() corrects it from
    // Ux.Language once auth resolves.
    this.initLanguage();
    this.mainStore.$subscribe((mutation, state) => {
      if (mutation.type === 'direct' && mutation.events?.key === 'theme') {
        this.mainStore._applyTheme();
      }
    });

    this.url_cif = this.url_params.has('cif') ? this.$FUSdecodeIfBase64(this.url_params.get('cif')) : null;
    // Who owns which Rey conversation is open — see the store's note on this field. The toolbar entry
    // resolves the Rey thread itself, whoever embeds it; the Spark entry is a host's blade, and only a
    // host that says so has stopped re-pinning its own thread id into the blade's URL.
    this.host_supports_rey_conversations = this.is_toolbar || this.url_params.get('rey_conversations') === '1';
    // Deployment opt-in that only SHOWS the conversation controls in embedded hosts (Navbar's New
    // topic + conversation list). Deliberately does not touch host_supports_rey_conversations above:
    // that flag also changes thread landing (?thread_id= becomes a default) and last-thread memory,
    // which a deployment-wide switch must not change. See the store's note on the new field.
    this.mainStore.host_shows_conversation_controls = window.__fusionReyConversationsInHost === true;
    // Deployment opt-in that shows the ⋯ options button in the Spark navbar (FUSION_OPTIONS_MENU_IN_HOST).
    // Purely additive visibility: without it nothing changes, because SHOW-OPTIONS from the host and the
    // toolbar's own trigger stay the only ways to open the OptionsMenu. See the store's note on the field.
    this.mainStore.host_shows_options_menu = window.__fusionOptionsMenuInHost === true;
    // OAuth door: if no JWT in the URL hash but the server passed one via the session-jwt prop,
    // use it directly. This is the direct-browser-entry path (no host app, no postMessage).
    var effectiveJwt = this.url_jwt;
    if (!effectiveJwt && this.sessionJwt) {
      effectiveJwt = this.$FUSdecodeIfBase64(this.sessionJwt);
    }
    if (effectiveJwt)
      this.authenticateViaAjax(effectiveJwt, this.url_cif);
    else
      this.auth_state = 'awaiting_auth';
  },
  mounted() {
    const self = this;
    window.addEventListener('message', this.handleWindowMessage);
    window.event_bus.$on('REQUEST-CONTEXT-FOR-MESSAGE', this.handleContextForMessageRequest);
    window.event_bus.$on('UNAUTHORIZED-CLICK', this.onUnauthorizedClick);
    document.addEventListener('visibilitychange', this.handleAnalyticsFlush);
    window.addEventListener('pagehide', this.handlePageHide);
    // Safety net for embedded webview hosts (e.g. ERA-Ignite) that occasionally race with
    // the asset pipeline on cold load and report window.innerHeight === 0 at the moment
    // our JS/CSS evaluates. When the host later allocates real pixel dimensions the
    // webview does not always fire a JS 'resize' event, leaving FusionBody.window_height
    // latched at 0. ResizeObserver fires on layout-box changes that 'resize' misses; we
    // dispatch a synthetic 'resize' so any existing window 'resize' listener (notably
    // FusionBody.handleResize) re-reads window.innerHeight. Debounced via $FUSdebounce
    // to avoid "ResizeObserver loop limit exceeded" warnings if a handler causes a reflow.
    if (typeof ResizeObserver !== 'undefined') {
      this.viewport_observer_handler = this.$FUSdebounce(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      this.viewport_observer = new ResizeObserver(this.viewport_observer_handler);
      this.viewport_observer.observe(document.documentElement);
    }
    if (!this.sessionJwt && (!this.wingman_config || (!this.url_jwt && !this.wingman_config?.FusionInitType?.AuthJwtToken))) {
      this.$FUSpostFusionMessage('REQUEST-CONTEXT', null, 'FUSION');
      this.context_request_nudge_id = setTimeout(() => {
        if (self.auth_state === 'awaiting_auth') {
          self.$FUSlog('Context Request still waiting. Sending new request ('+self.page_type+')', 'warn');
          self.$FUSpostFusionMessage('REQUEST-CONTEXT', null, 'FUSION');
        }
      }, this.context_request_timeout_ms / 2);
      this.context_request_last_chance_id = setTimeout(() => {
        if (self.auth_state === 'awaiting_auth') {
          self.$FUSlog('Context Request last chance. Sending final request ('+self.page_type+')', 'warn');
          self.$FUSpostFusionMessage('REQUEST-CONTEXT', null, 'FUSION');
        }
      }, this.context_request_timeout_ms - 1000);
      this.context_request_timeout_id = setTimeout(() => {
        if (self.auth_state === 'awaiting_auth') {
          self.$FUSlog('Fusion Access Denied: Context Request timeout ('+self.page_type+')', 'warn');
          self.auth_state = 'auth_failed';
          self.startContextRetryPolling();
        }
      }, this.context_request_timeout_ms);
    }
    this.removeJwtFromAddressBar();
  },
  beforeUnmount() {
    this.cleanupSocket();
    window.removeEventListener('message', this.handleWindowMessage);
    window.event_bus.$off('REQUEST-CONTEXT-FOR-MESSAGE', this.handleContextForMessageRequest);
    window.event_bus.$off('UNAUTHORIZED-CLICK', this.onUnauthorizedClick);
    document.removeEventListener('visibilitychange', this.handleAnalyticsFlush);
    window.removeEventListener('pagehide', this.handlePageHide);
    if (this.context_request_nudge_id)
      clearTimeout(this.context_request_nudge_id);
    if (this.context_request_last_chance_id)
      clearTimeout(this.context_request_last_chance_id);
    if (this.context_request_timeout_id)
      clearTimeout(this.context_request_timeout_id);
    if (this.context_retry_interval_id) {
      clearInterval(this.context_retry_interval_id);
      this.context_retry_interval_id = null;
    }
    this.clearAuthHint();
    if (this.viewport_observer) {
      this.viewport_observer.disconnect();
      this.viewport_observer = null;
      this.viewport_observer_handler = null;
    }
    // End session before shutdown so rey_session_ended is enqueued before the keepalive flush.
    // FusionToolbar also calls endReySession in its beforeUnmount — that call is idempotent.
    this.mainStore.endReySession();
    window.Analytics?.shutdown();
  },
  data() {
    return {
      auth_state: 'idle',
      client_jwt_token: null,
      last_processed_jwt: null,
      cif_processed: null,
      init_system_processed: null,
      init_user_guid_processed: null, // per-ERA-user id from the host; detects Ignite user switches that keep the same JWT
      awaiting_context_for_message: false,
      last_ai_thread_user_guid: null,
      last_context_json: null,
      context_request_timeout_id: null,
      context_request_nudge_id: null,
      context_request_last_chance_id: null,
      context_request_timeout_ms: 10000,
      context_retry_interval_id: null,
      // [NPM-72] Soft hint shown once retry polling has run for AUTH_HINT_DELAY_MS, so a slow
      // host app reads as "connecting" rather than as an unexplained spinner. Cleared wherever
      // polling is cleared.
      auth_hint_visible: false,
      auth_hint_timeout_id: null,
      show_debug_modal: false,
      unauthorized_click_count: 0,
      // Reactive label state: the copied flag drives debug_log_copy_label (computed) so the
      // button text follows the UI language instead of latching the string set at copy time.
      debug_log_copied: false,
      viewport_observer: null,
      viewport_observer_handler: null,
    }
  },
  computed: {
    ...mapStores(useMainStore),
    debug_log_copy_label() {
      return this.debug_log_copied ? this.$t('debug.copied') : this.$t('debug.copyToClipboard');
    },
    debug_logs() {
      return this.mainStore.debug_logs;
    },
    debug_logs_text() {
      return [...this.mainStore.debug_logs]
        .reverse()
        .map(e => {
          const time = new Date(e.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          return `[${time}] [${e.type.toUpperCase()}] ${e.message}`;
        })
        .join('\n');
    },
    wingman_config: {
      get() {
        return this.mainStore.wingman_config;
      },
      set(value) {
        this.mainStore.wingman_config = value;
      }
    },
    is_read_only: {
      get() {
        return this.mainStore.is_read_only;
      },
      set(value) {
        this.mainStore.is_read_only = value;
      }
    },
    context_screen_name: {
      get() {
        return this.mainStore.context_screen_name;
      },
      set(value) {
        this.mainStore.context_screen_name = value;
      }
    },
    context_control_id: {
      get() {
        return this.mainStore.context_control_id;
      },
      set(value) {
        this.mainStore.context_control_id = value;
      }
    },
    auth_token: {
      get() {
        return this.mainStore.token;
      },
      set(value) {
        this.mainStore.token = value;
      }
    },
    user_access_info: {
      get() {
        return this.mainStore.user_access_info;
      },
      set(value) {
        this.mainStore.user_access_info = value;
      }
    },
    active_dealer() {
      return this.mainStore.active_dealer;
    },
    has_fusion_entitlement() {
      return this.mainStore.has_fusion_entitlement;
    },
    is_toolbar() {
      return this.entryType === 'TOOLBAR';
    },
    is_trusted_login() {
      return this.entryType === 'TRUSTED_LOGIN';
    },
    auth_ready() {
      return this.auth_state === 'client_ready' && this.auth_token && this.user_access_info;
    },
    // [NPM-72] A refusal is only final once retry polling has given up. context_retry_interval_id
    // is reactive data, and the polling timeout clears it BEFORE the final auth_failed assignment,
    // so this flips to true exactly when the 60s budget is spent — never during the window in
    // which a late host token can still arrive. All ten auth_failed assignments stay untouched.
    auth_rejected() {
      return this.auth_state === 'auth_failed' && !this.context_retry_interval_id;
    },
    toolbar_auth_state() {
      if (this.auth_ready) return 'ready';
      if (this.auth_rejected) return 'unauthorized';
      if (this.auth_hint_visible) return 'connecting';
      return 'loading';
    },
    url_params() {
      let current_url = window && window.current_url ? window.current_url : null;
      if (current_url) {
        const url = new URL(current_url);
        if (url && url.searchParams)
          return url.searchParams;
      }
      
      return new URLSearchParams(window.location.search);
    },
    url_jwt() {
      return this.url_params.has('jwt') ? this.$FUSdecodeIfBase64(this.url_params.get('jwt')) : null;
    },
    url_cif: {
      get() {
        return this.mainStore.url_cif;
      },
      set(value) {
        this.mainStore.url_cif = value;
      },
    },
    host_supports_rey_conversations: {
      get() {
        return this.mainStore.host_supports_rey_conversations;
      },
      set(value) {
        this.mainStore.host_supports_rey_conversations = value;
      },
    },
    current_cif() {
      return this.mainStore.current_cif;
    },
    resolved_contact_info() {
      const ci = this.wingman_config?.ContactInfo;
      return {
        cdpId: ci?.CdpId || (this.url_params.has('cdp_id') ? this.$FUSdecodeIfBase64(this.url_params.get('cdp_id')) : null),
        email: ci?.Email || (this.url_params.has('email') ? this.$FUSdecodeIfBase64(this.url_params.get('email')) : null),
        phone: ci?.PhoneNumber || (this.url_params.has('phone') ? this.$FUSdecodeIfBase64(this.url_params.get('phone')) : null),
      };
    },
    jwt_token() {
      return this.client_jwt_token;
    },
    tool_configuration() {
      if (!this.setConfiguration || this.setConfiguration === 'null') return null;
      const config = JSON.parse(this.$FUSdecodeIfBase64(this.setConfiguration));
      if (!config.FuseConfigurations) config.FuseConfigurations = {};
      if (this.current_cif) config.FuseConfigurations.cif = this.current_cif;
      const ci = this.resolved_contact_info;
      if (ci.cdpId) config.FuseConfigurations.cdpId = ci.cdpId;
      if (ci.email) config.FuseConfigurations.email = ci.email;
      if (ci.phone) config.FuseConfigurations.phone = ci.phone;
      const threadId = this.url_params.has('thread_id') ? this.url_params.get('thread_id') : null;
      if (threadId) config.FuseConfigurations.threadId = threadId;
      return config;
    },
    page_type() {
      return this.tool_configuration?.PageType || (this.is_trusted_login?'TrustedLogin':null);
    },
    current_user_guid() {
      return this.user_access_info?.userId || null;
    },
    wingman_context_dto() {
      if (!this.wingman_config) return null;
      const toStr = val => (val != null) ? String(val) : '';
      const initType = this.wingman_config?.FusionInitType || {};
      const sparkAiChat = (this.wingman_config?.AllowedFuses || []).find(f => f.System === 'AVMessenger' && f.Function === 'SparkAIChat') || {};
      return {
        AuthCode: toStr(initType?.AuthCode),
        AuthJwtToken: toStr(this.jwt_token),
        userId: toStr(initType?.InitUserGuid),
        system: toStr(initType?.InitSystem),
        initRuntimeApp: toStr(initType?.InitRuntimeApp),
        initRuntimeAppVersion: toStr(initType?.InitRuntimeAppVersion),
        sessionId: toStr(this.wingman_config?.FusionSession),
        initRuntime: toStr(initType?.InitRuntime),
        systemId: toStr(initType?.InitSystemId),
        PageUrl: toStr(this.context_screen_name),
        ControlId: toStr(this.context_control_id),
        PageName: toStr(sparkAiChat?.Title),
        PageType: toStr(initType?.EntityType),
        DealerType: toStr(initType?.DealerType),
        DealerId: toStr(initType?.DealerId),
        DealerIdType: toStr(initType?.DealerIdType),
        EntityType: toStr(initType?.EntityType),
        EntityId: toStr(initType?.EntityId),
        EntityIdType: toStr(initType?.EntityIdType),
        ObjectID: toStr(initType?.ObjectID),
        ObjectType: toStr(initType?.InitLocationObjectType),
        Language: toStr(initType?.Language),
        initialMessage: '',
        moduleUrl: toStr(sparkAiChat?.ModuleUrl),
        title: toStr(sparkAiChat?.Title),
        ScreenConfiguration: [],
        dataApi: sparkAiChat?.DataApi || {},
        customerIdentification: {
          CIF: toStr(this.current_cif),
          ERAInfo: '',
          ForeignCustomerID: ''
        },
        PPSysId: toStr(initType?.PPSysId),
        StoreNumber: toStr(initType?.StoreNumber),
        BranchNumbers: Array.isArray(initType?.BranchNumbers) ? initType.BranchNumbers : [],
        StationNumber: toStr(initType?.StationNumber),
      };
    },
  },
  methods: {
    copyDebugLogs() {
      const textarea = document.createElement('textarea');
      textarea.value = this.debug_logs_text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.debug_log_copied = true;
      setTimeout(() => { this.debug_log_copied = false; }, 2000);
    },
    onUnauthorizedClick() {
      this.unauthorized_click_count++;
      if (this.unauthorized_click_count % 10 === 0)
        this.show_debug_modal = true;
    },
    handleUnauthorizedClick() {
      window.event_bus.$emit('UNAUTHORIZED-CLICK');
    },
    // Handle analytics flush on visibility changes. (WS-6)
    // hidden:  flush queued events so they are delivered before the tab is suspended.
    //          Session is NOT ended here — tab switches must not break session continuity.
    //          True page unload is handled by handlePageHide.
    // visible: restart a session if none is active. Covers the 3-hour timeout case where the
    //          session ended while idle and the user has now returned to focus. Multi-iframe
    //          adoption inside startReySession prevents redundant sessions.
    // TRUSTED_LOGIN frames never participate in session lifecycle — guarded at top.
    handleAnalyticsFlush() {
      if (this.is_trusted_login) return;
      if (document.visibilityState === 'hidden') {
        this.mainStore.touchReySession();
        window.Analytics?.flushOnBackground();
      } else if (document.visibilityState === 'visible') {
        if (!this.mainStore.rey_session_id) {
          const self = this;
          this.mainStore.initAnalytics().then(() => {
            // Re-check after async: another trigger may have started a session while we waited.
            if (!self.mainStore.rey_session_id)
              self.mainStore.startReySession();
          });
        }
      }
    },

    // True page unload — end the session and shut down analytics.
    // e.persisted === true means the page is entering the bfcache (back/forward nav), not unloading.
    handlePageHide(e) {
      if (!e.persisted) {
        this.mainStore.endReySession();
        window.Analytics?.shutdown();
      }
    },

    connectSocket() {
      this.mainStore.ConnectSocket();
    },
    cleanupSocket() {
      this.mainStore.CleanupSocket();
    },
    initLanguage() {
      this.mainStore.initLanguage();
    },
    // NOTE: the TRUSTED_LOGIN template outputs ("OK", "JWT token is invalid", "JWT token not
    // processed") are deliberately NOT localized — they are parsed server-side, not read by users.
    applyProfileTheme() {
      return this.mainStore.applyProfileTheme();
    },
    applyProfileLanguage() {
      return this.mainStore.applyProfileLanguage();
    },
    // Theme first, then language: applyProfileTheme() caches the settings doc into the store, so the
    // language read reuses it rather than firing a second settings/effective GET for the same doc.
    applyProfileUxPreferences() {
      const self = this;
      return Promise.resolve(this.applyProfileTheme())
        .then(() => self.applyProfileLanguage());
    },
    startContextRetryPolling() {
      if (this.context_retry_interval_id) return;
      const self = this;
      const startTime = Date.now();
      // [NPM-72] Arm the soft hint. Product decision: keep the hard refusal at the 60s timeout,
      // but stop the user staring at a bare spinner for that long.
      if (!this.auth_hint_timeout_id) {
        this.auth_hint_timeout_id = setTimeout(() => {
          self.auth_hint_timeout_id = null;
          if (self.context_retry_interval_id) self.auth_hint_visible = true;
        }, AUTH_HINT_DELAY_MS);
      }
      this.context_retry_interval_id = setInterval(() => {
        if (Date.now() - startTime >= 60000) {
          clearInterval(self.context_retry_interval_id);
          self.context_retry_interval_id = null;
          self.clearAuthHint();
          if (self.auth_state !== 'client_ready') {
            self.$FUSlog('FusionMain: Context retry polling timed out — showing auth failure ('+self.page_type+')', 'warn');
            self.auth_state = 'auth_failed';
          }
          return;
        }
        console.log('FusionMain: Retry polling — sending REQUEST-CONTEXT ('+self.page_type+')');
        self.$FUSpostFusionMessage('REQUEST-CONTEXT', null, 'FUSION');
      }, 4000);
    },
    /**
     * PostMessage handler for client-side auth path only.
     * Filters noise and handles UPDATE-CONTEXT to trigger auth flow.
     */
    /**
     * [FUS-474 / Security Backlog M3] A host may still hand the token over in ?jwt= (docs/authentication.md),
     * and the OAuth door's redirect leaves it in #jwt=. Once created()/mounted() have read it, take it out
     * of the address bar so it is not left in history or in a link someone copies. url_jwt reads no
     * reactive state, so its cached value is unaffected.
     */
    removeJwtFromAddressBar() {
      try {
        const url = new URL(window.location.href);
        const hashParams = new URLSearchParams(url.hash.substring(1));
        if (!url.searchParams.has('jwt') && !hashParams.has('jwt'))
          return;
        url.searchParams.delete('jwt');
        hashParams.delete('jwt');
        const hash = hashParams.toString();
        url.hash = hash ? '#' + hash : '';
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* history unavailable (e.g. a sandboxed frame) — nothing to clean */ }
    },
    handleWindowMessage(ev) {
      const receivedMessage = ev?.data || null;
      const messageType = receivedMessage?.type || null;
      const messageData = receivedMessage?.data || null;
      const messageSource = receivedMessage?.source || '';
      const messageKey = receivedMessage?.key || '';

      // Filter Vue DevTools, proxy, and internal VUE messages
      if (messageSource.indexOf('vue-devtools') >= 0 || messageSource.indexOf('proxy') >= 0 || messageKey.indexOf('_VUE_') >= 0)
        return;

      // Skip internal FUSION iframe messages
      if (['FUSION','FUSION-WEB'].indexOf(messageSource) >= 0)
        return;

      // FUS-474: only a host above us, from an allowed origin. UPDATE-CONTEXT signs the session in with
      // the JWT it carries, so a framing page or a frame we embed must not be able to send it.
      if (!isTrustedHostMessage(ev))
        return;
      
      // FUS-474 (Security Backlog M3): the type only — UPDATE-CONTEXT's payload carries the session JWT.
      console.log('[FusionMain] Received message ('+this.page_type+'): '+messageType);
      switch (messageType) {
        case 'UPDATE-CONTEXT':
          this.$FUSlog('FusionMain: UPDATE-CONTEXT received (' + this.page_type + ')', 'log');
          this.handleUpdateContext(messageData);
          break;
        case 'POWER-FOCUS-CHANGE':
          this.$FUSlog('FusionMain: POWER-FOCUS-CHANGE received: ' + JSON.stringify(this.$FUSredactSensitiveFields(messageData)), 'log');
          this.handlePowerFocusChange(messageData);
          break;
        case 'OPEN-THREAD':
          // A blade host (e.g. the WingCommander extension) asks us to open a specific thread. Used for
          // the Rey support-chat handoff: Internal Messages is a separate blade/iframe the host can't
          // reach in-app, so it posts this here. Bridge it onto the existing in-SPA event; the Internal
          // Messaging MessengerWindow opens it (openThreadFromEvent is scoped to that instance).
          if (messageData?.threadId)
            window.event_bus.$emit('MESSENGER-OPEN-THREAD', messageData.threadId);
          break;
        case 'OPEN-REY-CONVERSATION':
          // The Rey counterpart of OPEN-THREAD: the WingCommander extension forwarding an open that
          // started in another blade — a scheduled result opened from the results inbox in the Internal
          // Messages blade, say — into this Rey blade, since no blade can reach another's in-app bus.
          if (messageData?.threadId)
            this.requestSparkConversationOpen(messageData.threadId);
          break;
        default:
          break;
      }
    },

    /**
     * Extract JWT and CIF from UPDATE-CONTEXT message and trigger auth.
     * Also emits context DTO on the bus if a message-send context request is pending.
     */
    async handleUpdateContext(messageData) {
      if (!messageData?.wingmanConfig) return;

      // Clear the context request timers since we received a response
      if (this.context_request_nudge_id) {
        clearTimeout(this.context_request_nudge_id);
        this.context_request_nudge_id = null;
      }
      if (this.context_request_last_chance_id) {
        clearTimeout(this.context_request_last_chance_id);
        this.context_request_last_chance_id = null;
      }
      if (this.context_request_timeout_id) {
        clearTimeout(this.context_request_timeout_id);
        this.context_request_timeout_id = null;
      }
      if (this.context_retry_interval_id) {
        clearInterval(this.context_retry_interval_id);
        this.context_retry_interval_id = null;
      }
      this.clearAuthHint();
      
      this.wingman_config = messageData.wingmanConfig;
      const readOnly = this.wingman_config?.ReadOnly;
      this.is_read_only = (readOnly === true || readOnly === 'true');

      // If a message-send context request is pending, emit the converted context DTO on the bus
      if (this.awaiting_context_for_message) {
        this.awaiting_context_for_message = false;
        window.event_bus.$emit('CONTEXT-FOR-MESSAGE-RESPONSE', this.wingman_context_dto);
      }
      else {
        const initType = this.wingman_config?.FusionInitType;
        const newJwt = initType?.AuthJwtToken || null;
        const newInitSystem = initType?.InitSystem || null;

        if (newJwt && newJwt !== this.last_processed_jwt) {
            await this.authenticateViaAjax(newJwt, this.current_cif, newInitSystem);
        }
        else if (newJwt) {
            // [Ignite credential desync] An identical JWT does NOT mean an identical ERA user.
            // ERA-IGNITE users at one dealership share the DlrSecured account, so the host token's
            // sub/email can be identical across an Ignite logout → login as a different ERA user.
            // Server-side IsIdentitySwitch compares only sub/email and correctly answers
            // "same user" — but the ERA user DID change, and InitUserGuid is the one field in the
            // wingmanConfig that is per-ERA-user. Without this check Fusion keeps the previous
            // user's session: the "logged in as a different user, users did not change" reports.
            // The JWT is byte-identical, so re-running authenticateViaAjax would only hit the
            // token_already_processed short-circuit. What must happen: clear the SERVER session
            // (MVC /logout clears user_token; without it Authorize answers same_user forever)
            // and the client's user-scoped state, then re-authenticate.
            const initUserGuid = initType?.InitUserGuid || null;
            const eraUserChanged = initUserGuid
                && this.init_user_guid_processed
                && initUserGuid !== this.init_user_guid_processed;
            if (eraUserChanged) {
                this.$FUSlog('FusionMain: host JWT unchanged but InitUserGuid changed ('
                    + this.init_user_guid_processed + ' -> ' + initUserGuid
                    + ') — Ignite user switch under a shared DlrSecured account. Resetting session.', 'warn');
                this.init_user_guid_processed = initUserGuid;
                await this.mainStore.EndHostUserSession();
                await this.authenticateViaAjax(newJwt, this.current_cif, newInitSystem);
                return;
            }

            const cifChanged = this.current_cif !== this.cif_processed;
            const initSystemChanged = newInitSystem !== this.init_system_processed;
            if (cifChanged || initSystemChanged)
                await this.resolveCifAndUpdateState(this.current_cif, newInitSystem);
        }
        else if (!newJwt && (this.auth_state === 'awaiting_auth' || this.auth_state === 'auth_failed')) {
          this.$FUSlog('FusionMain: UPDATE-CONTEXT received with no JWT, cannot authenticate ('+this.page_type+')', 'warn');
          this.auth_state = 'auth_failed';
          this.startContextRetryPolling();
          return;
        }
        
        // Sync AI thread and context (toolbar-only, when auth is ready)
        await this.syncAIThreadAndContext();
      }
    },
    handlePowerFocusChange(messageData) {
      if (!messageData) return;
      this.context_screen_name = messageData.ScreenName || null;
      this.context_control_id = messageData.ControlId ?? null;
    },
    // Wrapper: hand an OPEN-REY-CONVERSATION from the host to the Spark panel.
    requestSparkConversationOpen(threadId) {
      this.mainStore.RequestSparkConversationOpen(threadId);
    },

    /**
     * Bus handler: MessengerWindow is requesting updated context before sending a message.
     * Sends REQUEST-CONTEXT to the parent window to trigger an UPDATE-CONTEXT response.
     */
    handleContextForMessageRequest() {
      console.log('[FusionMain] Received REQUEST-CONTEXT-FOR-MESSAGE, sending REQUEST-CONTEXT to parent');
      this.awaiting_context_for_message = true;
      this.$FUSpostFusionMessage('REQUEST-CONTEXT', null, 'FUSION');
    },

    /**
     * Clears the previous user's cached chat state from the store on a same-device user switch,
     * and resets the local AI-thread guard so syncAIThreadAndContext re-resolves the AI thread
     * for the incoming user (instead of reusing the stale current/last guid comparison).
     */
    clearUserScopedState() {
      this.mainStore.ClearUserScopedState();
      this.last_ai_thread_user_guid = null;
      // A user switch invalidates any in-flight getUserAccessInfo: bump the generation so a
      // pre-switch response that resolves later is discarded instead of overwriting the new
      // user's state.
      this._uaiGeneration = (this._uaiGeneration || 0) + 1;
    },
    /**
     * Toolbar-only: Fetch the AI thread for Spark.
     * Guarded by caller — only invoked when needed.
     */
    async getAIThread() {
      this.mainStore.ai_thread_loading = true;
      try {
        const response = await this.mainStore.GetAIThread('Fusion');
        if (response?.value?.id) {
          this.mainStore.ai_thread_obj = response.value;
          this.last_ai_thread_user_guid = this.current_user_guid;
        } else {
          this.mainStore.ai_thread_obj = null;
          this.$FUSlog('FusionMain: Fetching AI thread returned an invalid response', 'warn');
        }
      } catch (error) {
        this.$FUSlog('FusionMain: Error fetching AI thread: ' + (error?.message ?? error), 'error');
      } finally {
        this.mainStore.ai_thread_loading = false;
      }
    },
    async syncAIThreadAndContext() {
      if (!this.is_toolbar || this.auth_state !== 'client_ready') {
        return;
      }

      // Unentitled users (no FrontRunner/REYUSER) are 403'd on the Rey thread and its context
      // update; the toolbar shows the not-authorized message instead, so don't make the calls.
      if (!this.has_fusion_entitlement) {
        return;
      }

      // Fetch AI thread if needed (user changed or thread not loaded)
      const needsAiThread = !this.mainStore.ai_thread_obj || this.current_user_guid !== this.last_ai_thread_user_guid;
      if (needsAiThread) {
        await this.getAIThread();
      }

      // Update thread context if context changed and not mid-message-send
      if (!this.awaiting_context_for_message && this.wingman_context_dto) {
        const contextJson = JSON.stringify(this.wingman_context_dto);
        if (contextJson !== this.last_context_json) {
          this.updateThreadContext(contextJson);
        }
      }
    },

    /**
     * Toolbar-only: Send the current context DTO to the AI thread.
     * Guarded by caller — only invoked when context actually changed.
     */
    updateThreadContext(contextJson) {
      const threadId = this.mainStore.ai_thread_obj?.id || 'no_spark_thread_id';
      const updateContextDTO = {
        payload: this.wingman_context_dto,
        threadId: threadId,
      };
      this.mainStore.UpdateContext(updateContextDTO).then(() => {
        this.last_context_json = contextJson;
      }, error => {
        this.$FUSlog('FusionMain: Error updating thread context: ' + (error?.message ?? error), 'error');
      });
    },

    clearAuthHint() {
      if (this.auth_hint_timeout_id) {
        clearTimeout(this.auth_hint_timeout_id);
        this.auth_hint_timeout_id = null;
      }
      this.auth_hint_visible = false;
    },
    /**
     * [NPM-110 — REVERTED TO TELEMETRY] Observed-only: logs the gap between the token's exp
     * and the local clock so real-world skew and extension-side token caching are measurable
     * in the field. The client no longer REFUSES tokens. Under the silent-auth contract
     * ("never had to log into Rey; Rey was already logged in via dlrSecured") the host's
     * token can be near-expiry yet still valid server-side, and a client-side expiry
     * authority became stricter than the server ever was: issuer/browser clock drift widened
     * the rejection window, and an extension-cached page-load token turned the 60s
     * REQUEST-CONTEXT loop into a guaranteed unauthorized — the post-EU-sync login failures
     * with "various amounts of luck". The SERVER is the expiry authority: post the token;
     * a 401 drives the existing auth_failed → retry loop.
     */
    logJwtExpiryTelemetry(jwt) {
      try {
        const segment = String(jwt).split('.')[1];
        if (!segment) return;
        const b64 = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(segment.length / 4) * 4, '=');
        const payload = JSON.parse(atob(b64));
        if (typeof payload.exp !== 'number') return;
        const secondsLeft = Math.round((payload.exp * 1000 - Date.now()) / 1000);
        if (secondsLeft < 60) {
          this.$FUSlog('FusionMain: host token has ' + Math.round(secondsLeft) + 's left by local clock (telemetry only — posting regardless)', 'log');
        }
      } catch { /* telemetry must never break auth */ }
    },
    /**
     * Client-side auth: validate JWT, resolve CIF → dealer, fetch DAI.
     */
    async authenticateViaAjax(jwt, cif, initSystem = null) {
      this.logJwtExpiryTelemetry(jwt);

      this.auth_state = 'authenticating';
      this.last_processed_jwt = jwt;
      const encodedToken = btoa(jwt);

      try {
        const response = await this.mainStore.Authorize({ token: encodedToken });

        if (!response.success || !response.userToken) {
          this.$FUSlog('FusionMain: authorize failed:' + response.error, 'warn');
          this.auth_state = 'auth_failed';
          this.startContextRetryPolling();
          return;
        }

        if(this.is_trusted_login) {
          this.auth_state = 'client_ready';
          return;
        }
        this.auth_token = this.$FUSdecodeIfBase64(response.userToken);
        this.client_jwt_token = jwt;

        // When no CIF was passed (OAuth direct entry, no host app), defer the CIF check until
        // after getUserAccessInfo resolves the dealer list — the default-to-first-dealer logic
        // below fills it in. The host-app flow always provides a CIF, so this only affects
        // the OAuth door.

        // Determine if we need to re-resolve CIF → DealerGuid → DAI
        const isSameUser = response.action === 'same_user_session_updated'
                        || response.action === 'token_already_processed'
                        || response.action === 'token_refreshed';

        // A different user has authenticated in this same SPA instance (same device, after a
        // logout/login). Wipe the previous user's cached chat state before loading the incoming
        // user's data so threads/messages cannot leak across the switch. Any non-same-user action
        // (user_switched, or a trusted_login when the server session was reset on logout) is a
        // switch; the `user_access_info` guard skips the very first authentication, where there is
        // no prior user to clear.
        if (!isSameUser && this.user_access_info) {
          this.clearUserScopedState();
        }

        if (!isSameUser || !this.user_access_info) {
          // Capture the generation BEFORE the UAI call: getUserAccessInfo stamps a fresh one.
          // If a newer call or a user switch (clearUserScopedState) supersedes this flow while
          // we await, this `uaiResult` is false AND the generation has moved — in that case the
          // newer flow owns the outcome, so we bail WITHOUT touching auth_state / cif / socket /
          // sync (and WITHOUT marking auth_failed).
          const uaiGenBefore = this._uaiGeneration || 0;
          const uaiResult = await this.getUserAccessInfo();
          if (!uaiResult) {
            if (this._uaiGeneration !== uaiGenBefore + 1)
              return;
            this.$FUSlog('FusionMain: No User access info available — unauthorized', 'warn');
            this.auth_state = 'auth_failed';
            this.startContextRetryPolling();
            return;
          }
        }

        // OAuth door: when no CIF was passed by a host app (direct browser entry via /auth/login),
        // default to the user's first accessible dealer. The host-app flow always provides a CIF
        // via URL hash or postMessage, so this only fires for the OAuth entry point. Same
        // preference as ToolArgUtils.EnsureDealerContext server-side: first accessible dealer.
        if (!cif && !this.url_cif) {
          var dealers = this.user_access_info?.dealers || [];
          var firstCif = dealers[0]?.cifNumber;
          if (firstCif) {
            this.url_cif = firstCif;
            cif = firstCif;
            this.$FUSlog('FusionMain: No CIF in URL — defaulting to first accessible dealer: ' + firstCif, 'log');
          }
        }

        this.cif_processed = cif;
        this.init_system_processed = initSystem;
        this.init_user_guid_processed = this.wingman_config?.FusionInitType?.InitUserGuid || null;
        if(!Boolean(this.active_dealer)) {
          this.$FUSlog('FusionMain: No matched dealer — unauthorized', 'warn');
          this.auth_state = 'auth_failed';
          this.startContextRetryPolling();
          return;
        }
        
        this.auth_state = 'client_ready';
        this.connectSocket();
        // Auth fully resolved — both user_access_info and token are guaranteed set here, so apply
        // the user's saved Ux.Theme and Ux.Language. This broadcasts THEME-CHANGED to the host
        // toolbar and covers a first-ever load (empty sessionStorage, nothing for initTheme /
        // initLanguage to restore). Self-guards on missing token/userId; fire-and-forget so it
        // never blocks auth.
        this.applyProfileUxPreferences();
        await this.syncAIThreadAndContext();
      }
      catch (error) {
        this.$FUSlog('FusionMain: authorize error: ' + (error?.message ?? error), 'error');
        this.auth_state = 'auth_failed';
        this.startContextRetryPolling();
      }
    },
    /**
     * Silent CIF/init-system re-evaluation for same-JWT context changes.
     * Does NOT touch auth_state = 'authenticating', so the mounted controller stays alive.
     * Fetches UAI only if it was never loaded (e.g. auth originally failed before UAI was reached).
     */
    async resolveCifAndUpdateState(cif, initSystem) {
      if (!cif) {
        this.$FUSlog('FusionMain: No CIF available — unauthorized', 'warn');
        this.auth_state = 'auth_failed';
        this.startContextRetryPolling();
        return;
      }
      if (!this.user_access_info) {
        // Same supersession guard as authenticateViaAjax: if a newer UAI call or a user switch
        // (clearUserScopedState) bumped the generation while we awaited, this flow is stale and
        // the newer one owns the outcome — bail WITHOUT touching auth_state / cif / socket and
        // WITHOUT marking auth_failed.
        const uaiGenBefore = this._uaiGeneration || 0;
        const uaiResult = await this.getUserAccessInfo();
        if (!uaiResult) {
          if (this._uaiGeneration !== uaiGenBefore + 1)
            return;
          this.$FUSlog('FusionMain: No User access info available — unauthorized', 'warn');
          this.auth_state = 'auth_failed';
          this.startContextRetryPolling();
          return;
        }
      }

      this.cif_processed = cif;
      this.init_system_processed = initSystem;
      this.init_user_guid_processed = this.wingman_config?.FusionInitType?.InitUserGuid || null;
      if (!Boolean(this.active_dealer)) {
        this.$FUSlog('FusionMain: No matched dealer — unauthorized', 'warn');
        this.auth_state = 'auth_failed';
        this.startContextRetryPolling();
        return;
      }
      this.auth_state = 'client_ready';
      this.connectSocket();
      // Same as authenticateViaAjax: user_access_info and token are set here, so apply the saved
      // Ux.Theme and Ux.Language for first-ever loads. Self-guards on missing token/userId;
      // fire-and-forget.
      this.applyProfileUxPreferences();
    },
    getUserAccessInfo() {
      // Each call stamps a generation. If a newer call started or a user switch cleared the
      // scoped state in the meantime, this response is stale and must not overwrite
      // user_access_info or post MCP-ACCESS. A stale response simply returns false (no state
      // change) — the caller detects the supersession on its own and bails, so we do not adopt
      // another call's promise here (which would hang if that newest request never settles).
      this._uaiGeneration = (this._uaiGeneration || 0) + 1;
      const gen = this._uaiGeneration;
      return (async () => {
        try {
          const response = await this.mainStore.GetUserAccessInfo();

          if (gen !== this._uaiGeneration)
            return false;

          if (response?.success && response?.userAccessInfo) {
            this.user_access_info = response.userAccessInfo;
            this.$FUSpostFusionMessage('MCP-ACCESS', { mcpAccess: response.userAccessInfo.mcpAccess || [] });
            return true;
          }

          return false;
        } catch (e) {
          return false;
        }
      })();
    },
  },
}
</script>
